import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stamp } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { useSession } from '../../context/SessionContext';
import EtchPreview, { buildEtchDraft, buildMissionPayload, proposeStatement } from './EtchPreview.jsx';

/**
 * "Einprägen" (design spec §9.2) — the characteristic Etch action.
 *
 * Available per message and on a source selection. It opens the preview panel;
 * nothing at all is written until the user confirms there, and a normal chat
 * never creates a learning object on its own.
 *
 * On confirm the mission is POSTed immediately and the result is only reported
 * once the server acknowledged it. The undo is a real `DELETE /api/missions/:id`
 * (author-scoped) and stays available until the user acts on it — no countdown,
 * so it is not a time limit under WCAG 2.2.1, and nothing can be lost by closing
 * the tab in a deferral window.
 *
 * @param {object} props
 * @param {string} props.text - the answer (or snippet) the draft starts from.
 * @param {object[]} [props.sources] - resolved source descriptors for this answer.
 * @param {string|null} [props.defaultSourceId]
 * @param {string|null} [props.topic]
 * @param {'primary'|'secondary'|'ghost'|'signal'} [props.variant='ghost']
 * @param {'compact'|'default'|'large'} [props.size='compact']
 * @param {boolean} [props.iconOnly=false] - label stays in the accessible name.
 * @param {(mission: object) => void} [props.onSaved]
 */
export function EtchAction({
  text = '',
  sources = [],
  defaultSourceId = null,
  topic = null,
  variant = 'ghost',
  size = 'compact',
  iconOnly = false,
  onSaved,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { workspacePath } = useSession();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => buildEtchDraft());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | saved | undone | error
  const [savedMission, setSavedMission] = useState(null);

  const triggerRef = useRef(null);

  // The Undo control disappears when the status is cleared. Handing focus back
  // to the trigger first keeps the tab order where the user left it, instead of
  // resetting it to <body> (WCAG 2.4.3).
  const returnFocus = () => triggerRef.current?.focus?.();

  const openPreview = () => {
    setError(null);
    setDraft(
      buildEtchDraft({
        statement: proposeStatement(text),
        goal: t('etchPreview.defaultGoal'),
        sourceId: defaultSourceId || sources.find((s) => s.cited && s.state !== 'unreachable')?.id || null,
        form: 'standard',
        topic,
      })
    );
    setOpen(true);
  };

  const handleConfirm = async (confirmed) => {
    const payload = buildMissionPayload(confirmed, { t, sources, workspacePath });
    setSaving(true);
    setError(null);
    try {
      const { mission } = await apiClient.request('/missions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setOpen(false);
      setSavedMission(mission || null);
      // Only now — the confirmation names something that actually exists.
      setStatus('saved');
      onSaved?.(mission);
    } catch (err) {
      const message = err.message || t('states.error');
      setError(message);
      setStatus('error');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = async () => {
    const missionId = savedMission?.id;
    if (!missionId) return;
    setSaving(true);
    try {
      await apiClient.request(`/missions/${missionId}`, { method: 'DELETE' });
      setSavedMission(null);
      setStatus('undone');
    } catch (err) {
      const message = err.message || t('states.error');
      setError(message);
      setStatus('error');
      toast.error(message);
    } finally {
      setSaving(false);
      returnFocus();
    }
  };

  const dismissStatus = () => {
    setStatus('idle');
    setSavedMission(null);
    returnFocus();
  };

  const statusText = {
    saved: t('etchPreview.savedToast'),
    undone: t('etchPreview.undone'),
    error: error || t('states.error'),
  }[status];

  return (
    <span className="etch-etch-action">
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        onClick={openPreview}
        aria-label={iconOnly ? t('chat.etchAction') : undefined}
        data-action="etch"
      >
        <Stamp size={14} aria-hidden="true" />
        {!iconOnly && <span>{t('chat.etchAction')}</span>}
      </Button>

      {/* The live region carries text only. Controls live next to it: a button
          inside an aria-live region gets its label re-announced as part of the
          status, and it must stay reachable after the announcement. */}
      {status !== 'idle' && (
        <>
          <span className="etch-etch-action__status" role="status" aria-live="polite" data-state={status}>
            {statusText}
          </span>
          {status === 'saved' && savedMission?.id && (
            <>
              <Button variant="ghost" size="compact" onClick={handleUndo} disabled={saving}>
                {t('etchPreview.undo')}
              </Button>
              <span className="etch-etch-action__hint">{t('etchPreview.undoHint')}</span>
            </>
          )}
          <Button variant="ghost" size="compact" onClick={dismissStatus} disabled={saving}>
            {t('common.dismiss')}
          </Button>
        </>
      )}

      <EtchPreview
        open={open}
        onClose={() => setOpen(false)}
        draft={draft}
        onDraftChange={setDraft}
        onConfirm={handleConfirm}
        sources={sources}
        saving={saving}
        error={open ? error : null}
      />
    </span>
  );
}

export default EtchAction;
